/**
 * CRA-Check — one traffic light per ausgelieferte Produktinstanz.
 *
 * Two obligations have to hold at the same time, and the page keeps them apart
 * because they fail for different reasons and are fixed in different places:
 *
 *   Produktreife  — is the machinery even able to see vulnerabilities?
 *                   (Konfiguration, CPE-Identität, SBOM, Scan-Alter, Triage)
 *   Meldefristen  — for everything BETROFFEN: are the 24 h / 72 h / 14 d clocks
 *                   intact and has the customer been told?
 *
 * `unbekannt` is NOT green. A check that could not run (Dependency-Track down)
 * has not passed, so it is reported as its own state and counted separately — a
 * DT outage must never read as "alles konform".
 *
 * Everything shown here is derived server-side
 * (app/services/cra_check_service.py); this page adds no rules of its own, so the
 * verdict cannot drift between API consumers.
 */

import type { MessageDescriptor } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import CveLink from '../components/CveLink';
import Banner from '../components/Banner';
import Chip, { ChipList } from '../components/Chip';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import { Icon } from '../components/Icon';
import KpiDistribution, { type DistributionItem } from '../components/KpiDistribution';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import { errorMessage, fetchCraCheckOverview } from '../lib/api';
import { DASH, fmtDate } from '../lib/format';
import { intlLocale } from '../lib/locale';
import type { ChipTone } from '../components/Chip';
import type {
  CraCheck,
  CraCheckOverviewResponse,
  CraCheckRow,
  CraDeadline,
  CraState,
} from '../lib/types';
import { CRA_STATE_LABELS } from '../lib/types';

import './cracheck/cracheck.css';
import { backendLabel } from '../lib/backendLabels';

const STATE_CHIP: Readonly<Record<CraState, ChipTone>> = {
  ok: 'good',
  warnung: 'warn',
  fehler: 'alert',
  unbekannt: 'idle',
};

/** Sort order for the state filter buttons and the table. */
const STATE_RANK: Readonly<Record<CraState, number>> = {
  fehler: 0,
  unbekannt: 1,
  warnung: 2,
  ok: 3,
};

/** Tooltip on every „Unbekannt“ chip: a check that did not run has not passed. */
const UNKNOWN_TITLE = msg`Prüfung konnte nicht ausgeführt werden. Zählt nicht als konform.`;

/** CRA state chip; „Unbekannt“ carries the reason as its tooltip. `plain` is
 *  the text variant for the secondary Reife/Meldefristen columns. */
function StateChip({ state, plain = false }: { state: CraState; plain?: boolean }): ReactNode {
  const { i18n } = useLingui();
  return (
    <Chip tone={STATE_CHIP[state]} plain={plain} title={state === 'unbekannt' ? i18n._(UNKNOWN_TITLE) : undefined}>
      {CRA_STATE_LABELS[state]}
    </Chip>
  );
}

/**
 * The version part of a ProduktVersion label. CMDB labels a version as
 * "<Produkt> v<Nr>", so next to the product name only "v<Nr>" is new.
 */
function versionSuffix(produkt: string, produktversion: string): string {
  const version = produktversion.trim();
  const name = produkt.trim();
  if (name && version.toLowerCase().startsWith(name.toLowerCase())) {
    return version.slice(name.length).trim();
  }
  return version;
}

/** 'in 4,5 h', 'in 2 Tagen', or 'seit 6,0 h überfällig'. */
function Remaining({ deadline }: Readonly<{ deadline: CraDeadline }>): ReactNode {
  const { t } = useLingui();
  const hours = Math.abs(deadline.remaining_hours);
  const days = Math.round(hours / 24);
  const value = hours.toLocaleString(intlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (hours >= 48) {
    return deadline.overdue
      ? t`seit ${plural(days, { one: '# Tag', other: '# Tagen' })} überfällig`
      : t`in ${plural(days, { one: '# Tag', other: '# Tagen' })}`;
  }
  return deadline.overdue ? t`seit ${value} h überfällig` : t`in ${value} h`;
}

function CheckList({ checks }: { checks: readonly CraCheck[] }): ReactNode {
  return (
    <ul className="cra-checks">
      {checks.map((check) => (
        <li key={check.key}>
          <StateChip state={check.state} />
          <div>
            <div className="cra-check-label">{check.label}</div>
            <div className="cra-check-detail">{check.detail}</div>
          </div>
          {check.hint && check.state !== 'ok' ? (
            <div className="cra-check-hint">
              <Icon name="arrow-right" size={12} /> {check.hint}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** Status filter of the table: all, „Mit Befund“, one CRA state, or an
 *  overdue reporting deadline. The legend entries set the same filter. */
type StatusFilter = 'alle' | 'befund' | CraState | 'frist';

/** Labels are translated at render with `i18n._()`. */
const STATUS_OPTIONS: readonly { value: StatusFilter; label: MessageDescriptor }[] = [
  { value: 'alle', label: msg`Alle Instanzen` },
  { value: 'befund', label: msg`Mit Befund` },
  { value: 'ok', label: msg`Konform` },
  { value: 'fehler', label: msg`Fehler` },
  { value: 'warnung', label: msg`Warnung` },
  { value: 'frist', label: msg`Frist überfällig` },
  { value: 'unbekannt', label: msg`Nicht prüfbar` },
];

function hasOverdueDeadline(row: CraCheckRow): boolean {
  return row.deadlines.some((deadline) => deadline.overdue);
}

function matchesStatus(row: CraCheckRow, status: StatusFilter): boolean {
  if (status === 'alle') return true;
  if (status === 'befund') return row.state !== 'ok';
  if (status === 'frist') return hasOverdueDeadline(row);
  return row.state === status;
}

export function CraCheckPage(): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const [data, setData] = useState<CraCheckOverviewResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState<StatusFilter>('alle');
  const [search, setSearch] = useState('');

  const load = useCallback(async (mode: 'initial' | 'refresh'): Promise<void> => {
    if (mode === 'initial') setLoading(true);
    else setRefreshing(true);
    try {
      const response = await fetchCraCheckOverview();
      setData(response);
      setLoadError(null);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load('initial');
  }, [load]);

  const rows = useMemo((): CraCheckRow[] => {
    const all = data?.rows ?? [];
    const term = search.trim().toLowerCase();
    return all.filter((row) => {
      if (!matchesStatus(row, status)) return false;
      if (term === '') return true;
      return (
        row.kunde.toLowerCase().includes(term) ||
        row.produkt.toLowerCase().includes(term) ||
        row.seriennummer.toLowerCase().includes(term) ||
        row.kev_cves.some((cve) => cve.toLowerCase().includes(term))
      );
    });
  }, [data, status, search]);

  const distribution = useMemo((): DistributionItem[] => {
    const summary = data?.summary;
    if (!summary) return [];
    const overdue = summary.fristen_ueberfaellig;
    const warnHours = data.thresholds.deadline_warn_hours;
    return [
      { key: 'ok', label: t`Konform`, value: summary.by_state.ok ?? summary.compliant, tone: 'good', inBar: true },
      { key: 'fehler', label: t`Fehler`, value: summary.by_state.fehler ?? 0, tone: 'alert', inBar: true },
      { key: 'warnung', label: t`Warnungen`, value: summary.by_state.warnung ?? 0, tone: 'warn', inBar: true },
      {
        // Counted per instance (like every other entry), so the filtered list
        // shows exactly this many rows; the clocks behind it go in the tooltip.
        key: 'frist',
        label: t`Frist überfällig`,
        value: data.rows.filter(hasOverdueDeadline).length,
        tone: 'critical',
        title: t`${plural(overdue, { one: '# Meldefrist', other: '# Meldefristen' })} ohne erfasste Behördenmeldung verstrichen. Fristwarnung ${warnHours} h vorher.`,
      },
      {
        key: 'unbekannt',
        label: t`Nicht prüfbar`,
        value: summary.by_state.unbekannt ?? 0,
        tone: 'idle',
        inBar: true,
        title:
          t`Mindestens eine Prüfung konnte nicht ausgeführt werden, z. B. weil Dependency-Track nicht erreichbar ist. Zählt nicht als konform.`,
      },
    ];
    // `locale`: the labels are translated, re-build them on a language switch.
  }, [data, t, locale]);

  // A legend entry is marked while exactly its status filter is active (search
  // empty); clicking it opens that whole group, a second click clears it.
  const legendSelected = search.trim() === '' && status !== 'alle' && status !== 'befund' ? status : null;
  const onLegendSelect = (key: string): void => {
    const next = key as StatusFilter;
    setStatus(legendSelected === next ? 'alle' : next);
    setSearch('');
  };

  const columns = useMemo(
    (): readonly DataTableColumn<CraCheckRow>[] => [
      {
        key: 'state',
        header: 'CRA',
        width: '120px',
        sortValue: (row) => STATE_RANK[row.state],
        render: (row) => <StateChip state={row.state} />,
      },
      {
        key: 'kunde',
        header: t`Kunde`,
        sortValue: (row) => row.kunde.toLowerCase(),
        render: (row) => <strong>{row.kunde || DASH}</strong>,
      },
      {
        key: 'produkt',
        header: t`Produkt`,
        sortValue: (row) => row.produkt.toLowerCase(),
        render: (row) => {
          const version = versionSuffix(row.produkt, row.produktversion);
          if (!row.produkt) return row.produktversion || DASH;
          return (
            <>
              {row.produkt}
              {version ? <span className="muted"> {version}</span> : null}
            </>
          );
        },
      },
      {
        key: 'sn',
        header: t`Seriennr.`,
        width: '170px',
        sortValue: (row) => row.seriennummer,
        render: (row) => <span className="mono">{row.seriennummer || DASH}</span>,
      },
      {
        key: 'reife',
        header: t`Reife`,
        headerTitle: t`Produktreife: Konfiguration, CPE-Identität, SBOM, Scan-Alter, Triage`,
        width: '100px',
        sortValue: (row) => STATE_RANK[row.state_reife],
        render: (row) => <StateChip state={row.state_reife} plain />,
      },
      {
        key: 'fristen',
        header: t`Meldefristen`,
        width: '125px',
        sortValue: (row) => STATE_RANK[row.state_fristen],
        render: (row) => <StateChip state={row.state_fristen} plain />,
      },
      {
        key: 'kev',
        header: 'KEV',
        headerTitle: t`KEV-CVEs und davon als „Betroffen“ bewertete`,
        width: '190px',
        sortValue: (row) => row.betroffen_cves.length * 100 + row.kev_cves.length,
        render: (row) => {
          const affected = row.betroffen_cves.length;
          return row.kev_cves.length === 0 && affected === 0 ? (
            <span className="muted">{DASH}</span>
          ) : (
            <ChipList className="cra-kev">
              {row.kev_cves.length > 0 ? (
                <Chip tone="alert" count title={row.kev_cves.join(', ')}>
                  {row.kev_cves.length} KEV
                </Chip>
              ) : null}
              {affected > 0 ? (
                <Chip tone="warn" count title={row.betroffen_cves.join(', ')}>
                  {t`${affected} betroffen`}
                </Chip>
              ) : null}
            </ChipList>
          );
        },
      },
    ],
    // `locale`: the headers are translated, re-build them on a language switch.
    [t, locale],
  );

  const renderDetail = useCallback((row: CraCheckRow): ReactNode => {
    const { komponenten_count: componentCount, findings_total: findingCount, project_uuid: projectUuid } = row;
    const reife = row.checks.filter((check) => check.group === 'reife');
    const fristen = row.checks.filter((check) => check.group === 'fristen');
    return (
      <>
        <div className="cra-groups">
          <div>
            <h3><Trans>Produktreife</Trans></h3>
            <CheckList checks={reife} />
          </div>
          <div>
            <h3><Trans>Meldefristen (CRA Art. 14)</Trans></h3>
            <CheckList checks={fristen} />
            {row.deadlines.length > 0 ? (
              <ul className="cra-deadlines">
                {row.deadlines.map((deadline) => (
                  <li key={`${deadline.case_id}-${deadline.clock}`} title={deadline.titel}>
                    <Chip tone={deadline.overdue ? 'alert' : 'warn'}>{backendLabel(deadline.clock_label)}</Chip>
                    <span><Remaining deadline={deadline} /></span>
                    <span className="muted">{fmtDate(deadline.due)}</span>
                    <Link to={`/?view=fristen&case=${deadline.case_id}`}><Trans>Zeitleiste</Trans></Link>
                    {deadline.cves.length > 0 ? (
                      <ChipList>{deadline.cves.map((cve) => <CveLink key={cve} cve={cve} />)}</ChipList>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
        <p className="cra-detail-meta">
          <Plural
            value={componentCount}
            one="# Komponentenversion in der aufgelösten Konfiguration"
            other="# Komponentenversionen in der aufgelösten Konfiguration"
          />
          {findingCount > 0 ? (
            <> · <Plural value={findingCount} one="# Befund in Dependency-Track" other="# Befunde in Dependency-Track" /></>
          ) : null}
          {projectUuid ? <> · <Trans>Projekt {projectUuid}</Trans></> : null}
        </p>
      </>
    );
  }, []);

  if (loading) return <PageSpinner text={t`Prüft CRA-Konformität…`} />;

  const generatedAt = data ? fmtDate(data.generated_at) : null;
  const sbomDays = data?.thresholds.sbom_max_age_days ?? 0;
  const scanDays = data?.thresholds.scan_max_age_days ?? 0;
  const total = data?.rows.length ?? 0;
  const shown = rows.length;

  return (
    <>
      <PageHeader
        title="CRA-Check"
        sub={
          generatedAt !== null
            ? t`Geprüft am ${generatedAt} · SBOM veraltet nach ${plural(sbomDays, { one: '# Tag', other: '# Tagen' })}, Scan nach ${plural(scanDays, { one: '# Tag', other: '# Tagen' })}`
            : undefined
        }
        actions={
          <>
            {refreshing ? <Spinner label={t`Aktualisiert…`} /> : null}
            <button type="button" onClick={() => void load('refresh')} disabled={refreshing}>
              <Trans>Neu prüfen</Trans>
            </button>
          </>
        }
      />

      {loadError ? (
        <Banner kind="error" sub={t`Prüfen Sie die Verbindung zu CMDBuild.`}>
          {loadError}
        </Banner>
      ) : null}

      {data ? (
        <KpiDistribution
          total={data.summary.total}
          totalLabel={t`Instanzen geprüft`}
          items={distribution}
          label={t`CRA-Konformität der Instanzen`}
          selectedKey={legendSelected}
          onSelect={onLegendSelect}
          controls="cra-results"
        />
      ) : null}

      <section id="cra-results" className="card">
        <div className="card-head table-head">
          <h2><Trans>Konformität je Instanz</Trans></h2>
        </div>

        <div className="table-toolbar">
          <input
            type="search"
            value={search}
            placeholder={t`Kunde, Produkt, Seriennummer oder CVE suchen…`}
            aria-label={t`CRA-Check durchsuchen`}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as StatusFilter)}
            aria-label={t`Nach Status filtern`}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {i18n._(option.label)}
              </option>
            ))}
          </select>
          <div className="table-toolbar-options">
            {status !== 'alle' || search.trim() !== '' ? (
              <button type="button" className="link" onClick={() => { setStatus('alle'); setSearch(''); }}>
                <Trans>Filter zurücksetzen</Trans>
              </button>
            ) : null}
            <span className="table-count" aria-live="polite">
              {shown === total
                ? <Plural value={shown} one="# Instanz" other="# Instanzen" />
                : t`${shown} von ${plural(total, { one: '# Instanz', other: '# Instanzen' })}`}
            </span>
          </div>
        </div>

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.instanz_id}
          renderDetail={renderDetail}
          mobileBreakpoint={960}
          pageSize={10}
          paginationResetKey={JSON.stringify([status, search])}
          resetPageOnRowsChange={false}
          caption={t`CRA-Konformität je Produktinstanz`}
          emptyText={
            total === 0
              ? t`Keine Produktinstanzen vorhanden. Unter „Stammdaten“ anlegen oder per CSV importieren.`
              : t`Keine Instanz passt zu den Filtern.`
          }
        />
      </section>
    </>
  );
}

export default CraCheckPage;
